import { useEffect, useMemo, useState } from "react";
import { api, useAuth } from "@/context/AuthContext";
import { useCompany } from "@/context/CompanyContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Plus, Sparkles, Wrench, Search, Paperclip, CheckCircle2, Clock } from "lucide-react";
import { TaskDialog } from "@/components/TaskDialog";

const KIND_META = {
  CLEANING: { label: "Cleaning", icon: Sparkles, color: "#0891B2", bg: "bg-cyan-50", text: "text-cyan-900", border: "border-cyan-200" },
  MAINTENANCE: { label: "Maintenance", icon: Wrench, color: "#B45309", bg: "bg-amber-50", text: "text-amber-900", border: "border-amber-200" },
};

const STATUS_META = {
  PENDING: { label: "Pending", icon: Clock, color: "bg-slate-100 text-slate-700 border-slate-200" },
  DONE: { label: "Done", icon: CheckCircle2, color: "bg-emerald-100 text-emerald-800 border-emerald-200" },
};

export default function Tasks() {
  const { company } = useCompany();
  const { user: authUser } = useAuth();
  const isAdmin = (authUser?.role || "ADMIN").toUpperCase() === "ADMIN";
  const [tab, setTab] = useState("CLEANING");
  const [tasks, setTasks] = useState([]);
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(false);
  const [q, setQ] = useState("");
  const [propFilter, setPropFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/tasks");
      setTasks(r.data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    api.get("/properties").then((r) => setProperties(r.data));
    load();
  }, [company]);

  const filtered = useMemo(() => {
    return tasks
      .filter((t) => (t.kind || "CLEANING") === tab)
      .filter((t) => (propFilter === "all" ? true : t.property_booked === propFilter))
      .filter((t) => (statusFilter === "all" ? true : (t.status || "PENDING") === statusFilter))
      .filter((t) => {
        if (!q) return true;
        const s = q.toLowerCase();
        return (
          (t.property_booked || "").toLowerCase().includes(s) ||
          (t.assigned_to || "").toLowerCase().includes(s) ||
          (t.notes || "").toLowerCase().includes(s)
        );
      })
      .sort((a, b) => (b.start_date || "").localeCompare(a.start_date || ""));
  }, [tasks, tab, propFilter, statusFilter, q]);

  const counts = useMemo(
    () => ({
      CLEANING: tasks.filter((t) => (t.kind || "CLEANING") === "CLEANING").length,
      MAINTENANCE: tasks.filter((t) => t.kind === "MAINTENANCE").length,
    }),
    [tasks]
  );

  const openNew = () => {
    const today = new Date().toISOString().slice(0, 10);
    setEditing({ kind: tab, start_date: today, end_date: today, status: "PENDING" });
    setDialogOpen(true);
  };

  const openEdit = (t) => {
    setEditing(t);
    setDialogOpen(true);
  };

  const TabButton = ({ value }) => {
    const meta = KIND_META[value];
    const Icon = meta.icon;
    const active = tab === value;
    return (
      <button
        onClick={() => setTab(value)}
        className={`flex items-center gap-2 px-4 py-2.5 text-sm font-semibold border-b-2 -mb-px transition-colors ${
          active
            ? `border-current ${meta.text}`
            : "border-transparent text-slate-500 hover:text-slate-900"
        }`}
        style={active ? { color: meta.color, borderColor: meta.color } : {}}
        data-testid={`tasks-tab-${value}`}
      >
        <Icon className="w-4 h-4" />
        {meta.label}
        <span className="inline-flex items-center justify-center text-[10px] font-semibold bg-slate-100 text-slate-700 rounded-sm px-1.5 py-0.5 ml-1 min-w-[22px]">
          {counts[value]}
        </span>
      </button>
    );
  };

  return (
    <div className="p-4 sm:p-6 lg:p-10 space-y-6 max-w-[1400px]">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="label-eyebrow">Operations</div>
          <h1 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight mt-2 text-slate-900">
            Cleaning &amp; Maintenance
          </h1>
          <p className="text-sm text-slate-500 mt-2">
            Schedule and track jobs per property for <span className="font-semibold">{company}</span>.
          </p>
        </div>
        {isAdmin && (
        <Button
          onClick={openNew}
          className="rounded-sm bg-blue-900 hover:bg-blue-800 text-white active:scale-[0.98]"
          data-testid="tasks-new-button"
        >
          <Plus className="w-4 h-4 mr-2" /> New {KIND_META[tab].label} Task
        </Button>
        )}
      </div>

      {/* Tabs */}
      <div className="flex gap-1 border-b border-slate-200">
        <TabButton value="CLEANING" />
        <TabButton value="MAINTENANCE" />
      </div>

      {/* Filters */}
      <div className="bg-white border border-slate-200 rounded-sm p-4 grid grid-cols-1 md:grid-cols-3 gap-3">
        <div className="relative">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input
            placeholder="Search property, assignee, notes…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            className="pl-9 rounded-sm border-slate-300 h-10 focus:border-blue-900 focus:ring-blue-900"
            data-testid="tasks-search"
          />
        </div>
        <Select value={propFilter} onValueChange={setPropFilter}>
          <SelectTrigger className="rounded-sm border-slate-300 h-10" data-testid="tasks-property-filter">
            <SelectValue placeholder="Property" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All properties</SelectItem>
            {properties.map((p) => (
              <SelectItem key={p} value={p}>
                {p}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="rounded-sm border-slate-300 h-10" data-testid="tasks-status-filter">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            <SelectItem value="PENDING">Pending</SelectItem>
            <SelectItem value="DONE">Done</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Task list */}
      <div className="bg-white border border-slate-200 rounded-sm overflow-hidden">
        {loading ? (
          <div className="p-10 text-center text-sm text-slate-500">Loading…</div>
        ) : filtered.length === 0 ? (
          <div className="p-10 text-center text-sm text-slate-500">
            No {KIND_META[tab].label.toLowerCase()} tasks yet. Click "New {KIND_META[tab].label} Task" to schedule one.
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-slate-100 border-b border-slate-200 text-[10px] uppercase tracking-wider text-slate-600">
              <tr>
                <th className="text-left px-4 py-3 font-semibold">Property</th>
                <th className="text-left px-4 py-3 font-semibold">Start</th>
                <th className="text-left px-4 py-3 font-semibold">Finish</th>
                <th className="text-left px-4 py-3 font-semibold">Assigned to</th>
                <th className="text-left px-4 py-3 font-semibold">Notes</th>
                <th className="text-center px-4 py-3 font-semibold">Files</th>
                <th className="text-left px-4 py-3 font-semibold">Status</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((t) => {
                const StatusIcon = STATUS_META[t.status || "PENDING"].icon;
                const nAttach = (t.attachments || []).length;
                return (
                  <tr
                    key={t.id}
                    onClick={() => isAdmin && openEdit(t)}
                    className={`border-b border-slate-100 hover:bg-blue-50/40 transition-colors ${isAdmin ? "cursor-pointer" : "cursor-default"}`}
                    data-testid={`task-row-${t.id}`}
                  >
                    <td className="px-4 py-3 font-medium">{t.property_booked || "—"}</td>
                    <td className="px-4 py-3">{t.start_date || "—"}</td>
                    <td className="px-4 py-3">{t.end_date || t.start_date || "—"}</td>
                    <td className="px-4 py-3">{t.assigned_to || "—"}</td>
                    <td className="px-4 py-3 max-w-[280px] truncate text-slate-600">{t.notes || "—"}</td>
                    <td className="px-4 py-3 text-center">
                      {nAttach > 0 ? (
                        <span className="inline-flex items-center gap-1 text-xs text-slate-700">
                          <Paperclip className="w-3 h-3" /> {nAttach}
                        </span>
                      ) : (
                        <span className="text-slate-400">—</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <span
                        className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-sm border text-[10px] font-semibold uppercase tracking-wider ${STATUS_META[t.status || "PENDING"].color}`}
                      >
                        <StatusIcon className="w-3 h-3" />
                        {STATUS_META[t.status || "PENDING"].label}
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      <TaskDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        initial={editing}
        properties={properties}
        onSaved={load}
      />
    </div>
  );
}
